import type { ChatToolEvent, CopilotChatResult } from '@fos/shared/contracts/copilot';
import type { CopilotExecutedStep } from '@fos/shared/contracts/copilot-actions';
import { useSyncExternalStore } from 'react';
import type { CopilotErrorInfo } from './copilot-ui';

/**
 * Rozmowa z Copilotem w tej sesji przeglądarki (Fala 10, bąbel AI): jedna na kartę, zostaje po zamknięciu panelu,
 * przejściu na inny ekran i odświeżeniu strony (sessionStorage — znika po zamknięciu karty; osobno dla osoby
 * i workspace'u). Zapisane rozmowy na serwerze (historia promptów) to osobny mechanizm.
 *
 * Po odświeżeniu strony podglądy do potwierdzenia (wpis czasu, zadanie, zlecenie dla Claude, wybór) nie wracają jako
 * aktywne przyciski — rozmowa jest do przeczytania, a ponowne potwierdzenie mogłoby utworzyć drugi rekord.
 *
 * 10.10.2026 (błąd karty planu): wynik „Wykonaj” (`executed`) i „Cofnij” (`undone`) są częścią tury, nie stanu
 * komponentu — panel po zamknięciu odmontowuje treść (Radix Dialog), więc karta po ponownym otwarciu, zmianie ekranu
 * albo odświeżeniu pokazuje wynik, a nie znowu „Wykonaj”.
 */

export interface ChatTurn {
  id: string;
  /** `note` — znacznik w rozmowie (np. zmiana kontekstu ekranu); nie trafia do AI */
  role: 'user' | 'assistant' | 'note';
  content: string;
  result?: CopilotChatResult;
  error?: CopilotErrorInfo;
  /** osoba potwierdziła podgląd z tej odpowiedzi (zadanie, czas, zlecenie) — plakietka bąbla znika */
  resolved?: boolean;
  /** odtworzona z sessionStorage: bez aktywnych podglądów */
  restored?: boolean;
  /** wynik planu wykonanego z tej odpowiedzi (`copilot.executePlan`); klucz = numer zdarzenia w `toolEvents` */
  executed?: Record<string, CopilotExecutedStep[]>;
  /** „Cofnij” wykonane z tej odpowiedzi; klucz = przycisk (np. `plan:0`, `event:2`, `all`), wartość = podsumowanie */
  undone?: Record<string, string>;
  /** podgląd z tej odpowiedzi potwierdzony (zadanie, czas, zlecenie, notatka); klucz = numer zdarzenia */
  confirmed?: Record<string, ChatConfirmed>;
}

/** Wynik potwierdzenia podglądu w rozmowie („Dodano zadanie #12 …” z linkiem). */
export interface ChatConfirmed {
  label: string;
  href: string | null;
  /** zadanie przypisane innej osobie (dopisek „Przypisane do …”) */
  assignee?: { userId: string; name: string } | null;
}

interface ChatStoreState {
  scope: string | null;
  turns: ChatTurn[];
}

const STORAGE_PREFIX = 'fos.copilot.chat.v1:';
/** Najwyżej tyle tur w pamięci sesji (do AI i tak idzie ostatnie 20 wiadomości). */
const MAX_TURNS = 60;

let state: ChatStoreState = { scope: null, turns: [] };
const listeners = new Set<() => void>();
let seq = 0;

function emit(next: ChatStoreState): void {
  state = next;
  for (const l of listeners) l();
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** Bez podglądów do potwierdzenia (odtworzenie po odświeżeniu = tylko do czytania). */
function readOnlyEvent(e: ChatToolEvent): ChatToolEvent {
  // Fala 10 (copilot-actions): także plan działań do potwierdzenia (`plan`)
  // Fala 10 (notatki klienta): także notatka do potwierdzenia (`notePreview`)
  const { timePreview: _t, taskPreview: _k, delegatePreview: _d, choices: _c, plan: _p, notePreview: _n, ...rest } = e;
  return rest as ChatToolEvent;
}

function persist(): void {
  const s = storage();
  if (!s || !state.scope) return;
  try {
    const turns = state.turns
      .slice(-MAX_TURNS)
      .map((t) =>
        t.result ? { ...t, result: { ...t.result, toolEvents: t.result.toolEvents.map(readOnlyEvent) } } : t,
      );
    s.setItem(STORAGE_PREFIX + state.scope, JSON.stringify(turns));
  } catch {
    // pełna pamięć albo zablokowane — rozmowa zostaje tylko w tej karcie
  }
}

/** Słownik z pamięci sesji tylko z poprawnymi wartościami (inaczej brak). */
function recordOf(value: unknown, ok: (v: unknown) => boolean): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entries = Object.entries(value).filter(([, v]) => ok(v));
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function restore(scope: string): ChatTurn[] {
  const s = storage();
  if (!s) return [];
  try {
    const raw = s.getItem(STORAGE_PREFIX + scope);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (t): t is ChatTurn =>
          Boolean(t) &&
          typeof t === 'object' &&
          typeof (t as ChatTurn).id === 'string' &&
          ['user', 'assistant', 'note'].includes((t as ChatTurn).role) &&
          typeof (t as ChatTurn).content === 'string',
      )
      .map((t) => ({
        ...t,
        restored: true,
        executed: recordOf(t.executed, Array.isArray) as ChatTurn['executed'],
        undone: recordOf(t.undone, (v) => typeof v === 'string') as ChatTurn['undone'],
        confirmed: recordOf(
          t.confirmed,
          (v) => Boolean(v) && typeof v === 'object' && typeof (v as ChatConfirmed).label === 'string',
        ) as ChatTurn['confirmed'],
      }));
  } catch {
    return [];
  }
}

export function newTurnId(): string {
  seq += 1;
  return `t${Date.now().toString(36)}${seq}`;
}

export const copilotChat = {
  /** osoba + workspace (sesja); zmiana = inna rozmowa (odtworzona z sessionStorage) */
  setScope(scope: string | null): void {
    if (scope === state.scope) return;
    emit({ scope, turns: scope ? restore(scope) : [] });
  },
  update(fn: (turns: ChatTurn[]) => ChatTurn[]): void {
    emit({ ...state, turns: fn(state.turns).slice(-MAX_TURNS) });
    persist();
  },
  /** podgląd z odpowiedzi potwierdzony (plakietka „czeka na potwierdzenie” znika) */
  resolve(turnId: string): void {
    copilotChat.update((turns) => turns.map((t) => (t.id === turnId ? { ...t, resolved: true } : t)));
  },
  /** plan z odpowiedzi wykonany: wynik kroków zostaje w rozmowie (także po zamknięciu panelu i odświeżeniu) */
  recordExecuted(turnId: string, eventIndex: number, steps: CopilotExecutedStep[]): void {
    copilotChat.update((turns) =>
      turns.map((t) =>
        t.id === turnId ? { ...t, resolved: true, executed: { ...t.executed, [String(eventIndex)]: steps } } : t,
      ),
    );
  },
  /** podgląd z odpowiedzi potwierdzony: wynik zostaje w rozmowie (po ponownym otwarciu bez drugiego „Dodaj”) */
  recordConfirmed(turnId: string, eventIndex: number, confirmed: ChatConfirmed): void {
    copilotChat.update((turns) =>
      turns.map((t) =>
        t.id === turnId ? { ...t, resolved: true, confirmed: { ...t.confirmed, [String(eventIndex)]: confirmed } } : t,
      ),
    );
  },
  /** „Cofnij” z odpowiedzi wykonane (podsumowanie zamiast przycisku) */
  recordUndone(turnId: string, key: string, summary: string): void {
    copilotChat.update((turns) =>
      turns.map((t) => (t.id === turnId ? { ...t, undone: { ...t.undone, [key]: summary } } : t)),
    );
  },
  clear(): void {
    emit({ ...state, turns: [] });
    const s = storage();
    if (s && state.scope) {
      try {
        s.removeItem(STORAGE_PREFIX + state.scope);
      } catch {
        // bez pamięci sesji
      }
    }
  },
  getSnapshot(): ChatStoreState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** testy */
  reset(): void {
    const s = storage();
    try {
      if (s) {
        for (const key of Object.keys(s)) if (key.startsWith(STORAGE_PREFIX)) s.removeItem(key);
      }
    } catch {
      // bez pamięci sesji
    }
    emit({ scope: null, turns: [] });
  },
};

export function useCopilotChat(): ChatStoreState {
  return useSyncExternalStore(copilotChat.subscribe, copilotChat.getSnapshot, copilotChat.getSnapshot);
}

const WAITING_STATES = new Set(['needs_confirmation', 'needs_choice']);

/**
 * Copilot czeka na potwierdzenie osoby (plakietka bąbla): ostatnia odpowiedź ma podgląd do potwierdzenia albo wybór,
 * nie potwierdzony i nie odtworzony po odświeżeniu.
 */
export function awaitingConfirmation(turns: readonly ChatTurn[]): boolean {
  const last = [...turns].reverse().find((t) => t.role === 'assistant');
  if (!last?.result || last.resolved || last.restored) return false;
  return WAITING_STATES.has(last.result.state);
}
